type SomeFunction = (this: any, ...args: any[]) => any;
type VoidFunction<F extends SomeFunction> = (this: ThisParameterType<F>, ...args: [...Parameters<F>]) => void;
type CancelableVoidFunction<F extends SomeFunction> = VoidFunction<F> & {
    cancel: () => void,
};

export function debounce<F extends SomeFunction>(callable: F, delay: number): CancelableVoidFunction<typeof callable> {
    let timeout: ReturnType<typeof setTimeout>;

    function wrapper(this: ThisParameterType<typeof callable>, ...args: Parameters<typeof callable>) {
        wrapper.cancel();

        timeout = setTimeout(context => callable.apply(context, args), delay, this);
    }

    wrapper.cancel = () => {
        if (timeout !== undefined) {
            clearTimeout(timeout);
        }
    };

    return wrapper;
}

export function throttle<F extends SomeFunction>(callable: F, delay: number): CancelableVoidFunction<typeof callable> {
    let timeout: ReturnType<typeof setTimeout>,
        isThrottled = false,
        haveToExecute = false,
        savedArgs: Parameters<typeof callable> | null = null,
        savedThis: ThisParameterType<typeof callable> | undefined | null = null;

    function wrapper(this: ThisParameterType<typeof callable> | undefined, ...args: Parameters<typeof callable>) {
        if (isThrottled) {
            haveToExecute = true;
            savedArgs = args;
            savedThis = this;// eslint-disable-line @typescript-eslint/no-this-alias

            return;
        }

        callable.apply(this, args);

        isThrottled = true;

        timeout = setTimeout(function () {
            isThrottled = false;

            if (haveToExecute) {
                wrapper.apply(savedThis ?? undefined, (savedArgs ?? []) as any);

                savedArgs = savedThis = null;
                haveToExecute = false;
            }
        }, delay);
    }

    wrapper.cancel = () => {
        if (timeout !== undefined) {
            clearTimeout(timeout);
        }
    };

    return wrapper;
}
